import React, { useState } from 'react';
import { 
  Box, 
  Text, 
  Button, 
  Stack,
  Group,
  TextInput,
  PasswordInput,
  SegmentedControl,
  Code,
} from '@mantine/core';
import { useForm } from '@mantine/form';
import { IconKey, IconBrandGoogle, IconTestPipe } from '@tabler/icons';
import { showNotification } from '@mantine/notifications';
import { generateOAuthUrl, validateSubdomain } from '../utils/oauthUrlGenerator';
import { SubdomainInput } from './SubdomainInput';
import { Instructions } from './Instructions';
import { 
  AuthType,
  ApiKeyField,
  ApiKeyConfig,
} from '../types';
import { useIntegrationConfig, useTestIntegrationCredentials } from '../../../../hooks/useIntegrations';

import { Integration, AvailableIntegration } from '@bageltechnologies/bagel-common';
import { isBagelAdmin } from '../../../../utils';
import { useAuth0 } from '@auth0/auth0-react';

interface AuthenticationTabProps {
  integration: {
    available: AvailableIntegration;
    connected?: Integration;
  };
  onConnect: (formData: any) => Promise<void>;
  onDisconnect: () => Promise<void>;
  isConnecting: boolean;
  isDisconnecting: boolean;
  isUpdatingConfig: boolean;
}

interface FormValues {
  authType: AuthType;
  subdomain: string;
  apiKeyFields: Record<string, string>;
  clientId: string;
  clientSecret: string;
}

const defaultApiKey: ApiKeyField = {
  key: 'apiKey',
  label: 'API Key',
  description: 'Enter your API key to authenticate',
  placeholder: 'Enter your API key',
  type: 'password',
  required: true,
  validate: {
    minLength: 8,
    message: 'API Key must be at least 8 characters long'
  }
};

const defaultApiKeyConfig: ApiKeyConfig = {
  name: "API Key Authentication",
  description: "Connect using your API key credentials",
  requiresSubdomain: false,
  fields: [defaultApiKey]
};
  
export const AuthenticationTab: React.FC<AuthenticationTabProps> = ({
  integration,
  onConnect,
  onDisconnect,
  isConnecting,
  isDisconnecting,
  isUpdatingConfig,
}) => {
  const { available, connected } = integration;
  const apiKey = available.apiKey || defaultApiKeyConfig;
  const [oauthUrl, setOauthUrl] = useState<string>('');
  const [isClientConfigSaved, setIsClientConfigSaved] = useState<boolean>(
    !!(connected?.oauthOverride?.clientId && connected?.oauthOverride?.clientSecret)
  );
  const { mutateAsync: updateConfigMutation, isPending: updatingConfig } = useIntegrationConfig();
  const { mutateAsync: testCredentials, isPending: isTestingCredentials } = useTestIntegrationCredentials();
  const auth0 = useAuth0();
  const isBagelUser = isBagelAdmin(auth0?.user);

  const handleTestCredentials = async () => {
    try {
      const result = await testCredentials(available.key);
      console.log('Test credentials result:', result);
      
      if (result.valid) {
        showNotification({
          title: 'Connection Test Successful',
          message: result.message || `Successfully connected to ${available.name}`,
          color: 'green',
          autoClose: 3000,
        });
      } else {
        showNotification({
          title: 'Connection Test Failed',
          message: result?.message || `Failed to connect to ${available.name}`,
          color: 'red',
          autoClose: 5000,
        });
      }
    } catch (error: any) {
      console.error('Failed to test credentials:', error);
      showNotification({
        title: 'Connection Test Error',
        message: error?.response?.data?.message || error?.message || `Unable to test connection to ${available.name}`,
        color: 'red',
        autoClose: 5000,
      });
    }
  };

  // Get initial values from connected data if available
  const initialValues = {
    authType: (connected?.tokenType || available.supportedAuthTypes?.[0]) as AuthType,
    subdomain: connected?.subdomain || '',
    apiKeyFields: {},
    clientId: connected?.oauthOverride?.clientId || '',
    clientSecret: connected?.oauthOverride?.clientSecret || '',
  };

  const form = useForm<FormValues>({
    initialValues: initialValues,
    validate: {
      subdomain: (value, values) => {
        if (((values.authType === AuthType.OAUTH2 && available.oauth?.requiresSubdomain) ||
         (values.authType === AuthType.API_KEY && available.apiKey?.requiresSubdomain)) &&
          !value) {
          return 'Subdomain is required';
        }
        if (value && available.oauth?.requiresSubdomain) {
          const validation = validateSubdomain(available, value);
          if (!validation.isValid) {
            return validation.error;
          }
        }
        return null;
      },
      clientId: (value, values) => {
        if (values.authType === AuthType.OAUTH2 && available.oauth?.manuallySetIdSecret && !value) {
          return 'Client ID is required';
        }
        return null;
      },
      clientSecret: (value, values) => {
        if (values.authType === AuthType.OAUTH2 && available.oauth?.manuallySetIdSecret && !value) {
          return 'Client Secret is required';
        }
        return null;
      },
      apiKeyFields: {
        ...Object.fromEntries(
          (apiKey?.fields || []).map(field => [
            field.key,
            (value: string) => {
              if (field.required && !value) {
                return `${field.label} is required`;
              }

              if (value) {
                switch (field.type) {
                case 'url':
                  if (!/^https?:\/\/.+/.test(value)) {
                    return 'Please enter a valid URL starting with http:// or https://';
                  }
                  break;
                case 'email':
                  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
                    return 'Please enter a valid email address';
                  }
                  break;
                }

                if (field.validate) {
                  if (field.validate.pattern) {
                    const regex = new RegExp(field.validate.pattern);
                    if (!regex.test(value)) {
                      return field.validate.message || 'Invalid format';
                    }
                  }

                  if (field.validate.minLength && value.length < field.validate.minLength) {
                    return `Must be at least ${field.validate.minLength} characters`;
                  }

                  if (field.validate.maxLength && value.length > field.validate.maxLength) {
                    return `Must be no more than ${field.validate.maxLength} characters`;
                  }
                }
              }

              return null;
            }
          ])
        )
      }
    },
    validateInputOnChange: true,
    validateInputOnBlur: true,
  });

  // Update form when connection status changes
  React.useEffect(() => {
    if (connected?.tokenObject) {
      // Only update if the current auth type matches the connected type
      const isApiKey = connected.tokenType === AuthType.API_KEY || connected.tokenObject.type === 'api-key';
      
      if ((isApiKey && form.values.authType === AuthType.API_KEY) ||
          (!isApiKey && form.values.authType === AuthType.OAUTH2)) {
        
        // Set API key fields from tokenObject
        const apiKeyFields: Record<string, string> = {};
        Object.entries(connected.tokenObject).forEach(([key, value]) => {
          if (key !== 'type' && key !== 'token_type' && typeof value === 'string') {
            apiKeyFields[key] = value;
          }
        });
        
        form.setFieldValue('apiKeyFields', apiKeyFields);
        
        // Set subdomain if exists
        if (connected.subdomain) {
          form.setFieldValue('subdomain', connected.subdomain);
        }

        // Set client ID and secret if exists
        if (connected.oauthOverride?.clientId) {
          form.setFieldValue('clientId', connected.oauthOverride.clientId);
        }
        if (connected.oauthOverride?.clientSecret) {
          form.setFieldValue('clientSecret', connected.oauthOverride.clientSecret);
        }

        setIsClientConfigSaved(!!(connected.oauthOverride?.clientId && connected.oauthOverride?.clientSecret));
      }
    }
  }, [connected, form.values.authType]);

  // Generate OAuth URL when auth type or subdomain changes
  React.useEffect(() => {
    if (form.values.authType === AuthType.OAUTH2) {
      try {
        // Only generate URL if subdomain is valid when required
        if (available.oauth?.requiresSubdomain && !form.values.subdomain) {
          setOauthUrl('');
          return;
        }

        if (available.oauth?.requiresSubdomain) {
          const validation = validateSubdomain(available, form.values.subdomain);
          if (!validation.isValid) {
            setOauthUrl('');
            return;
          }
        }
        const params: any = {
          subdomain: form.values.subdomain || undefined,
          redirectUri: window.__RUNTIME_CONFIG__.REACT_APP_OAUTH_CALLBACK,
          runtimeConfig: window.__RUNTIME_CONFIG__
        }
        // Use override client ID if available, otherwise use form value for manual setup
        if (available.oauth?.manuallySetIdSecret) {
          params.clientId = connected?.oauthOverride?.clientId || form.values.clientId;
        }
        const url = generateOAuthUrl(
          integration.available,
          params
        );
        setOauthUrl(url);
      } catch (error) {
        console.error('Failed to generate OAuth URL:', error);
        setOauthUrl('Error generating OAuth URL');
      }
    }
  }, [form.values.authType, form.values.subdomain, integration.available, available]);

  const handleSubmit = form.onSubmit(async (values:any) => {
    if (values.authType === AuthType.API_KEY) {
      const formData = {
        authType: AuthType.API_KEY,
        apiKeyFields: values.apiKeyFields,
        ...(values.subdomain && { subdomain: values.subdomain })
      };
      await onConnect(formData);
    }
  });

  const handleOAuthConnect = () => {
    if (oauthUrl) {
      window.location.href = oauthUrl;
    }
  };

  const handleSaveClientConfig = async () => {
    try {
      // Call updateConfigMutation with client credentials
      await updateConfigMutation({
        key: integration.available.key,
        config: {
          subdomain: form.values.subdomain,
          oauthOverride: {
            clientId: form.values.clientId,
            clientSecret: form.values.clientSecret
          }
        }
      });
      setIsClientConfigSaved(true);
      setOauthUrl(generateOAuthUrl(integration.available, {
        subdomain: form.values.subdomain,
        redirectUri: window.__RUNTIME_CONFIG__.REACT_APP_OAUTH_CALLBACK,
        runtimeConfig: window.__RUNTIME_CONFIG__,
        clientId: form.values.clientId
      }));
    } catch (error) {
      console.error('Failed to save client configuration:', error);
    }
  };

  const showConnectUI = !connected?.tokenObject || connected?.status === 'disabled';

  return (
    <Box>
      <form onSubmit={handleSubmit}>
        <Stack spacing="xl">
          {/* Auth Type Selector */}
          {available?.supportedAuthTypes?.length > 0 && <Box>
            <Group position="apart" align="flex-start">
              <Box>
                <Text weight={500} size="sm" mb="xs" color='dimmed'>Authentication Method</Text>
            
                <SegmentedControl
                  {...form.getInputProps('authType')}
                  disabled={!showConnectUI}
                  data={(available?.supportedAuthTypes)?.map(type => ({
                    label: (
                      <Group spacing="xs" noWrap>
                        {type === AuthType.OAUTH2 ? (
                          <IconBrandGoogle size={16} />
                        ) : (
                          <IconKey size={16} />
                        )}
                        <Text>{type === AuthType.OAUTH2 ? 'OAuth 2.0' : 'API Key'}</Text>
                      </Group>
                    ),
                    value: type
                  }))}
                />
              </Box>
              
              {['active', 'setup'].includes(connected?.status || '') && (
                <Box sx={{ justifyItems: 'right', gap: 4 }}>
                  <Button
                    variant={connected?.tested?.result ? 'outline' : 'filled'}
                    color={
                      connected?.tested?.result ? 'green' : 
                        connected?.tested?.result === false ? 'red' : 
                          'indigo'
                    }
                    loading={isTestingCredentials}
                    onClick={handleTestCredentials}
                    type="button"
                    radius="md"
                    size="sm"
                    rightIcon={<IconTestPipe size={16} />}
                    sx={
                      !(connected as any)?.tested ? {
                        animation: 'pulse 2s infinite',
                        '@keyframes pulse': {
                          '0%': { boxShadow: '0 0 0 0 #4c6ef570' },
                          '70%': { boxShadow: '0 0 0 10px rgba(77, 171, 247, 0)' },
                          '100%': { boxShadow: '0 0 0 0 rgba(77, 171, 247, 0)' },
                        },
                      } : undefined
                    }
                  >
                    {connected?.tested?.result ? 'Test Again' : 
                      connected?.tested?.result === false ? 'Retry Test' :
                        'Test Connection'}
                  </Button>
                  {connected?.tested?.result && <Text size="xs" mt={4} color="dimmed">Last tested: {new Date((connected as any).tested.date).toLocaleString()}</Text>}
                </Box>
              )}
            </Group>
          </Box>}
          

          {/* OAuth Configuration */}
          {form.values.authType === AuthType.OAUTH2 && (
            <Box>
              {showConnectUI && (
                <Text weight={500} size="md" mb="md">OAuth 2.0 Configuration</Text>
              )}
              <Stack spacing="md">

                {/* OAuth Instructions */}
                {available.oauth?.instructions && showConnectUI && (
                  <Instructions 
                    instructions={available.oauth.instructions} 
                    title="Setup Instructions" 
                  />
                )}

                {available.oauth?.requiresSubdomain && (
                  <SubdomainInput
                    {...form.getInputProps('subdomain')}
                    placeholder={available.oauth.placeholderText || "Enter your domain"}
                    label="Domain"
                    description={available.oauth.description || "Enter your domain to connect"}
                    value={form.values.subdomain}
                    disabled={!showConnectUI}
                  />
                )}

                {/* Manual Client ID and Secret configuration */}
                {available.oauth?.manuallySetIdSecret && showConnectUI && (
                  <>
                    <TextInput
                      {...form.getInputProps('clientId')}
                      label="Client ID"
                      description="Enter your application's client ID"
                      placeholder="Enter client ID"
                      required
                    />
                    <PasswordInput
                      {...form.getInputProps('clientSecret')}
                      label="Client Secret"
                      description="Enter your application's client secret"
                      placeholder="Enter client secret"
                      required
                      autoComplete="new-password"
                    />
                    <Button
                      color="indigo"
                      onClick={handleSaveClientConfig}
                      loading={updatingConfig}
                      disabled={!form.values.clientId || !form.values.clientSecret}
                      type="button"
                      variant="outline"
                    >
                      Save Credentials
                    </Button>
                  </>
                )}

                {showConnectUI && !available.oauth?.manuallySetIdSecret && (
                  <Text size="sm" color="dimmed">
                  Click connect to authorize with {available.name}.
                  </Text>
                )}

                {available.oauth?.manuallySetIdSecret && showConnectUI && (
                  <Text size="sm" color="dimmed">
                  First save your client credentials, then connect to authorize with {available.name}.
                  </Text>
                )}


                {showConnectUI && isBagelUser && (
                  <Box>
                    <Text size="sm" weight={500} mb="xs">Debug - OAuth URL:</Text>
                    <Code block style={{ wordBreak: 'break-all' }}>
                      {oauthUrl || (available.oauth?.requiresSubdomain ? 'Enter domain to generate URL...' : 'Generating URL...')}
                    </Code>
                  </Box>)}
                {!showConnectUI && (<Text size="sm" color="dimmed">
                  Deactivates {available.name} integration.
                </Text>)}
                {/* OAuth Connect Button */}
                <Group position="left">
                  {['active', 'setup'].includes(connected?.status || '') ? (
                    <Button
                      color="red"
                      loading={isDisconnecting}
                      onClick={onDisconnect}
                      type="button"
                      radius="md"
                    >
                      Disconnect from {available.name}
                    </Button>
                  ) : (
                    <Button
                      color="blue"
                      loading={isConnecting || isUpdatingConfig}
                      onClick={handleOAuthConnect}
                      type="button"
                      disabled={
                        !oauthUrl || 
                        (available.oauth?.manuallySetIdSecret && !isClientConfigSaved)
                      }
                      radius="md"
                    >
                      Connect with {available.name}
                    </Button>
                  )}
                </Group>
              </Stack>
            </Box>
          )}

          {/* API Key Configuration */}
          {form.values.authType === AuthType.API_KEY && (
            <Box>
              <Text weight={500} size="md" mb="md">API Key Configuration</Text>
              <Stack spacing="md">
                {/* API Key Instructions */}
                {available.apiKey?.instructions && showConnectUI && (
                  <Instructions 
                    instructions={available.apiKey.instructions} 
                    title="Setup Instructions" 
                  />
                )}

                {available.apiKey?.requiresSubdomain && (
                  <SubdomainInput
                    {...form.getInputProps('subdomain')}
                    placeholder={available.apiKey.placeholderText || "Enter your domain"}
                    label="Domain"
                    description={available.apiKey.description || "Enter your domain to connect"}
                    value={form.values.subdomain}
                    disabled={!showConnectUI}
                  />
                )}
                { apiKey?.fields?.map((field) => {
                  const InputComponent = field.type === 'password' ? PasswordInput : TextInput;
                  return (
                    <InputComponent
                      key={field.key}
                      {
                        ...({
                          ...form.getInputProps(`apiKeyFields.${field.key}`),
                          value: connected && connected.status === 'active' ? "••••••••••••••••" : form.values.apiKeyFields[field.key]
                        })
                      }
                      label={field.label}
                      description={field.description}
                      placeholder={field.placeholder}
                      required={connected && connected.status === 'active' ? false : field.required}
                      autoComplete={field.type === 'password' ? 'new-password' : undefined}
                      disabled={connected && connected.status === 'active'}
                    />
                  );
                })}
                {/* API Key Connect Button */}
                <Group position="left" mt="md">
                  {['active', 'setup'].includes(connected?.status || '') ? (
                    <Button
                      color="red"
                      loading={isDisconnecting}
                      onClick={onDisconnect}
                      type="button"
                      radius="md"
                    >
                      Disconnect API Key
                    </Button>
                  ) : (
                    <Button
                      color="blue"
                      loading={isConnecting || isUpdatingConfig}
                      type="submit"
                    >
                      Connect with API Key
                    </Button>
                  )}
                </Group>
              </Stack>
            </Box>
          )}
        </Stack>
      </form>
    </Box>
  );
};

export default AuthenticationTab; 